import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Gift, CheckSquare } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const PENDING_HINT = "Refund still pending at the payment provider — available once it fails or is cancelled";

const FAILED_LABELS = { canceled: "Cancelled by Stripe", failed: "Failed at provider" };

const RefundStateBadge = ({ state, status }) => {
  if (state === "pending") {
    return <span className="inline-block px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 font-medium">Pending at provider</span>;
  }
  const label = FAILED_LABELS[status] || "Refund error";
  return <span className="inline-block px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium">{label}</span>;
};


// iter198: FA Refund Failures tab. Lists tickets whose Stripe refund
// branch failed (e.g. source account REFUND_FAILED_INSUFFICIENT_FUNDS)
// and gives three per-row actions: retry, convert the pending amount to
// a voucher, or mark it as settled off-Stripe (bank transfer / cash etc.).
export default function FARefundFailures() {
  const { authAxios } = useAuth();
  const [rows, setRows] = useState(null);
  const [busyRow, setBusyRow] = useState(null);
  const [voucherRow, setVoucherRow] = useState(null);
  const [resolveRow, setResolveRow] = useState(null);
  const [voucherExpiry, setVoucherExpiry] = useState(() => {
    // Default voucher expiry: 1 year from today (FA can override).
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [resolveMethod, setResolveMethod] = useState("bank_transfer");
  const [resolveReference, setResolveReference] = useState("");
  const [resolveNote, setResolveNote] = useState("");

  const load = async (reconcile = false) => {
    try {
      if (reconcile) {
        await authAxios.post("/ticketing/fa/refund-failures/reconcile-all").catch(() => {});
      }
      const r = await authAxios.get("/ticketing/fa/refund-failures");
      setRows(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load refund failures");
      setRows([]);
    }
  };
  useEffect(() => { load(true); }, []); // eslint-disable-line

  const retry = async (ticketId) => {
    setBusyRow(ticketId);
    try {
      const r = await authAxios.post(`/ticketing/fa/tickets/${ticketId}/retry-stripe-refund`);
      if (r.data.status === "succeeded") toast.success("Refund cleared");
      else if (r.data.status === "terminal") toast.error(`Terminal: ${r.data.reason || "unknown"}`);
      else toast(`Still pending: ${r.data.reason || "retry queued"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusyRow(null);
    }
  };

  const convertToVoucher = async () => {
    if (!voucherRow) return;
    setBusyRow(voucherRow.id);
    try {
      await authAxios.post(`/ticketing/fa/tickets/${voucherRow.id}/convert-to-voucher`, {
        expires_at: voucherExpiry,
      });
      toast.success("Converted to voucher");
      setVoucherRow(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally {
      setBusyRow(null);
    }
  };

  const markResolved = async () => {
    if (!resolveRow) return;
    if (!resolveReference.trim()) { toast.error("Reference required"); return; }
    setBusyRow(resolveRow.id);
    try {
      await authAxios.post(`/ticketing/fa/tickets/${resolveRow.id}/mark-refund-resolved`, {
        method: resolveMethod, reference: resolveReference, note: resolveNote,
      });
      toast.success("Marked resolved");
      setResolveRow(null);
      setResolveReference(""); setResolveNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark resolved failed");
    } finally {
      setBusyRow(null);
    }
  };

  if (rows === null) return <p className="text-slate-400 text-center py-8">Loading refund failures...</p>;

  return (
    <div data-testid="fa-refund-failures">
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-[#1e3a5f]">Refund Failures</h3>
            <p className="text-xs text-slate-500">
              Card refunds that didn&rsquo;t clear (e.g. source payment-method had insufficient funds).
              Auto-retried every 15 min; after 48h an escalation email is sent to all FAs.
            </p>
          </div>
          <button
            onClick={() => load(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="fa-refund-failures-reload"
          >
            <RefreshCw className="w-3 h-3" />Reload
          </button>
        </div>
        {rows.length === 0 && (
          <p className="py-8 text-center text-slate-400 text-sm">No pending refund failures.</p>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="py-1">Buyer</th>
                <th>Event / Tier</th>
                <th className="text-right">Pending</th>
                <th>Reason</th>
                <th className="text-right">Retries</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`fa-refund-failure-row-${r.id}`}>
                  <td className="py-2">
                    <div className="font-medium">{r.buyer_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{r.buyer_email}</div>
                  </td>
                  <td>
                    <div>{r.event_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{r.tier_name}</div>
                  </td>
                  <td className="text-right font-bold">€{Number(r.pending_stripe_amount || 0).toFixed(2)}</td>
                  <td className="text-[11px] max-w-[320px]" data-testid={`fa-refund-failure-reason-${r.id}`}>
                    <RefundStateBadge state={r.refund_state} status={r.stripe_refund_status} />
                    {r.stripe_refund_error && <div className="text-amber-700">{r.stripe_refund_error}</div>}
                  </td>
                  <td className="text-right text-[11px] text-slate-500">
                    {r.stripe_refund_retry_count || 0}
                    {r.stripe_refund_terminal ? <span className="ml-1 text-red-600">terminal</span> : null}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      onClick={() => retry(r.id)}
                      disabled={busyRow === r.id}
                      className="px-2 py-1 rounded-md text-[11px] border border-slate-200 text-slate-700 hover:bg-slate-50 mr-1 disabled:opacity-50"
                      data-testid={`fa-refund-failure-retry-${r.id}`}
                    >Retry</button>
                    <button
                      onClick={() => setVoucherRow(r)}
                      disabled={busyRow === r.id || r.refund_state === "pending"}
                      title={r.refund_state === "pending" ? PENDING_HINT : undefined}
                      className="px-2 py-1 rounded-md text-[11px] border border-amber-200 text-amber-700 hover:bg-amber-50 mr-1 inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`fa-refund-failure-voucher-${r.id}`}
                    ><Gift className="w-3 h-3" /> Voucher</button>
                    <button
                      onClick={() => setResolveRow(r)}
                      disabled={busyRow === r.id || r.refund_state === "pending"}
                      title={r.refund_state === "pending" ? PENDING_HINT : undefined}
                      className="px-2 py-1 rounded-md text-[11px] border border-green-200 text-green-700 hover:bg-green-50 inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`fa-refund-failure-resolve-${r.id}`}
                    ><CheckSquare className="w-3 h-3" /> Mark resolved</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Convert-to-voucher dialog */}
      <Dialog open={!!voucherRow} onOpenChange={(o) => !o && setVoucherRow(null)}>
        <DialogContent data-testid="fa-refund-voucher-dialog">
          <DialogHeader>
            <DialogTitle>Convert refund to voucher</DialogTitle>
            <DialogDescription>
              Issue a voucher of <strong>€{Number(voucherRow?.pending_stripe_amount || 0).toFixed(2)}</strong>
              {" "}for <strong>{voucherRow?.buyer_email}</strong>. The pending Stripe refund will be marked cleared
              and the buyer will receive the code by email.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <label className="text-xs text-slate-500 block mb-1">Expires on</label>
            <input
              type="date"
              value={voucherExpiry}
              onChange={(e) => setVoucherExpiry(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="fa-refund-voucher-expiry"
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setVoucherRow(null)}
              className="px-3 py-1.5 rounded-md text-xs border border-slate-200"
            >Cancel</button>
            <button
              onClick={convertToVoucher}
              disabled={busyRow === voucherRow?.id || !voucherExpiry}
              className="px-3 py-1.5 rounded-md text-xs bg-amber-600 text-white disabled:opacity-50"
              data-testid="fa-refund-voucher-confirm"
            >Issue voucher</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark-resolved dialog */}
      <Dialog open={!!resolveRow} onOpenChange={(o) => !o && setResolveRow(null)}>
        <DialogContent data-testid="fa-refund-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Mark refund as settled</DialogTitle>
            <DialogDescription>
              Confirm that <strong>€{Number(resolveRow?.pending_stripe_amount || 0).toFixed(2)}</strong>
              {" "}was returned to <strong>{resolveRow?.buyer_email}</strong> outside Stripe.
              The buyer will receive a confirmation email referencing the method + reference below.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Method</label>
              <select
                value={resolveMethod}
                onChange={(e) => setResolveMethod(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="fa-refund-resolve-method"
              >
                <option value="bank_transfer">Bank transfer / SEPA</option>
                <option value="paypal">PayPal</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Reference</label>
              <input
                type="text"
                value={resolveReference}
                onChange={(e) => setResolveReference(e.target.value)}
                placeholder="e.g. SEPA txn id, PayPal txn id, receipt #"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="fa-refund-resolve-reference"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Note (optional)</label>
              <textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                rows={2}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="fa-refund-resolve-note"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setResolveRow(null)}
              className="px-3 py-1.5 rounded-md text-xs border border-slate-200"
            >Cancel</button>
            <button
              onClick={markResolved}
              disabled={busyRow === resolveRow?.id || !resolveReference.trim()}
              className="px-3 py-1.5 rounded-md text-xs bg-green-600 text-white disabled:opacity-50"
              data-testid="fa-refund-resolve-confirm"
            >Mark resolved</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
